/**
 * Shared Workday reference-list handling.
 *
 * The position form and the hire form read the same `position_reference_options`
 * lists — the position form by reference type, the hire form by following a
 * link the schema hands it — so the shape, the labelling and the key separator
 * live here rather than in both.
 */

/** FormBuilder flattens nested schemas to `Section-Field`. */
export const FORM_KEY_SEPARATOR = '-';

export interface WorkdayReference {
  id: string;
  name: string;
  /** Populated for teams only; elsewhere null. */
  manager?: string | { name?: string } | null;
}

const managerName = (manager: WorkdayReference['manager']) =>
  typeof manager === 'string' ? manager : manager?.name;

/**
 * Workday sends several lists as raw ids — `San_Francisco`, `Part_time`,
 * `Salary_Plan` — so underscores become spaces.
 *
 * Deliberately generic: no code dictionary and no case rewriting, so `CW` stays
 * `CW` and `Global Modern Services, Inc. (USA)` is untouched. Display only; the
 * submitted value is always the raw id.
 */
const humaniseWorkdayName = (name: unknown): string =>
  String(name ?? '')
    .replace(/_/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

/**
 * `{ id, label }` is the shape FormBuilder's `asyncSelect` reads. The manager is
 * folded into the label — a bare `SUP-ORG-459` means nothing to a recruiter.
 */
export const toWorkdayOption = (option: WorkdayReference) => {
  const manager = managerName(option.manager);
  const label = humaniseWorkdayName(option.name);
  return { id: option.id, label: manager ? `${label} — ${manager}` : label };
};
